/**
 * Normalized preferences catalog + O(1) selection state helpers.
 * Built once from GET allergy_dislikes — avoids rebuilding the full tree on every tap.
 *
 * UI shows categories and sub-categories only (sub-categories appear as selectable "items").
 * Selection state and save payloads still use underlying inventory item ids.
 */
import { getApiText } from '../../../shared/i18n';
import { extractInventoryItemsFromSubCategory, resolveInventoryItemId } from './inventoryPreferenceTypes';

export interface CatalogItem {
  id: number;
  title: string;
}

export interface CatalogSubCategory {
  id: number;
  title: string;
  items: CatalogItem[];
}

export interface CatalogCategory {
  id: number;
  title: string;
  subCategories: CatalogSubCategory[];
}

export interface SelectionState {
  checked: boolean;
  indeterminate: boolean;
  selected_count: number;
  total_count: number;
}

/** User-facing chip for a fully addressable sub-category row. */
export interface SelectedSubCategoryChip {
  key: string;
  title: string;
  category_title: string;
  item_ids: number[];
}

/** Flat index of sub-categories (user-visible "items") for search + chips. */
export interface FlatSubCategoryEntry {
  sub_category_id: number;
  title: string;
  category_id: number;
  category_title: string;
  item_ids: number[];
}

export interface PreferenceListRow {
  key: string;
  title: string;
  breadcrumb?: string;
  /** All inventory item ids in this sub-category; toggling selects or clears the whole group. */
  sub_category_item_ids: number[];
}

function resolveCategoryId(category: any, index: number): number {
  return typeof category?.id === 'number' ? category.id : index;
}

function resolveSubCategoryId(sub_category: any, index: number): number {
  return typeof sub_category?.id === 'number' ? sub_category.id : index;
}

function labelFromRaw(raw: any): string {
  return getApiText(raw, 'title', 'a_title') || raw?.title || '';
}

export function buildPreferencesCatalog(categories_raw: any[]): CatalogCategory[] {
  if (!Array.isArray(categories_raw) || categories_raw.length === 0) {
    return [];
  }

  return categories_raw.map((category, category_index) => {
    const sub_categories_raw = category?.sub_categories ?? category?.subCategories ?? [];
    const sub_categories: CatalogSubCategory[] = (Array.isArray(sub_categories_raw) ? sub_categories_raw : []).map(
      (sub_category: any, sub_index: number) => {
        const items_raw = extractInventoryItemsFromSubCategory(sub_category);
        const items: CatalogItem[] = items_raw
          .map((item: any) => {
            const id = resolveInventoryItemId(item);
            if (id <= 0) {
              return null;
            }
            return {
              id,
              title: labelFromRaw(item) || String(id),
            };
          })
          .filter((item): item is CatalogItem => item != null);

        return {
          id: resolveSubCategoryId(sub_category, sub_index),
          title: labelFromRaw(sub_category),
          items,
        };
      },
    );

    return {
      id: resolveCategoryId(category, category_index),
      title: labelFromRaw(category),
      subCategories: sub_categories,
    };
  });
}

export function getSubCategoryItemIds(sub_category: CatalogSubCategory): number[] {
  return sub_category.items.map(item => item.id);
}

export function getCategoryItemIds(category: CatalogCategory): number[] {
  const ids: number[] = [];
  category.subCategories.forEach(sub_category => {
    ids.push(...getSubCategoryItemIds(sub_category));
  });
  return ids;
}

export function isSubCategoryFullySelected(item_ids: number[], selected_ids: Set<number>): boolean {
  if (item_ids.length === 0) {
    return false;
  }
  return item_ids.every(id => selected_ids.has(id));
}

export function computeSelectionState(selected_ids: Set<number>, item_ids: number[]): SelectionState {
  const total_count = item_ids.length;
  if (total_count === 0) {
    return { checked: false, indeterminate: false, selected_count: 0, total_count: 0 };
  }

  let selected_count = 0;
  for (const id of item_ids) {
    if (selected_ids.has(id)) {
      selected_count += 1;
    }
  }

  if (selected_count === 0) {
    return { checked: false, indeterminate: false, selected_count, total_count };
  }
  if (selected_count === total_count) {
    return { checked: true, indeterminate: false, selected_count, total_count };
  }
  return { checked: false, indeterminate: true, selected_count, total_count };
}

export function filterPreferencesCatalog(catalog: CatalogCategory[], query: string): CatalogCategory[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return catalog;
  }

  return catalog.filter(category => {
    if (category.title.toLowerCase().includes(normalized)) {
      return true;
    }
    return category.subCategories.some(sub_category => sub_category.title.toLowerCase().includes(normalized));
  });
}

/** Flat sub-category index — built once per catalog. */
export function buildFlatSubCategoryIndex(catalog: CatalogCategory[]): FlatSubCategoryEntry[] {
  const rows: FlatSubCategoryEntry[] = [];
  catalog.forEach(category => {
    category.subCategories.forEach(sub_category => {
      const item_ids = getSubCategoryItemIds(sub_category);
      if (item_ids.length === 0) {
        return;
      }
      rows.push({
        sub_category_id: sub_category.id,
        title: sub_category.title.trim() || `#${sub_category.id + 1}`,
        category_id: category.id,
        category_title: category.title,
        item_ids,
      });
    });
  });
  return rows;
}

export function resolveSelectedSubCategoryChips(
  index: FlatSubCategoryEntry[],
  selected_ids: Set<number> | number[],
): SelectedSubCategoryChip[] {
  const lookup = selected_ids instanceof Set ? selected_ids : new Set(selected_ids);
  const out: SelectedSubCategoryChip[] = [];

  for (const entry of index) {
    const has_selection = entry.item_ids.some(id => lookup.has(id));
    if (!has_selection) {
      continue;
    }
    out.push({
      key: `${entry.category_id}-${entry.sub_category_id}`,
      title: entry.title,
      category_title: entry.category_title,
      item_ids: entry.item_ids,
    });
  }

  return out;
}

export function buildCategoryListRows(category: CatalogCategory): PreferenceListRow[] {
  const rows: PreferenceListRow[] = [];
  category.subCategories.forEach(sub_category => {
    const sub_item_ids = getSubCategoryItemIds(sub_category);
    if (sub_item_ids.length === 0) {
      return;
    }
    const sub_title = sub_category.title.trim();
    rows.push({
      key: `sub-${category.id}-${sub_category.id}`,
      title: sub_title.length > 0 ? sub_title : `#${sub_category.id + 1}`,
      sub_category_item_ids: sub_item_ids,
    });
  });
  return rows;
}

export function buildSearchListRows(index: FlatSubCategoryEntry[], query: string): PreferenceListRow[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return [];
  }

  return index
    .filter(entry => {
      if (entry.title.toLowerCase().includes(normalized)) {
        return true;
      }
      return entry.category_title.toLowerCase().includes(normalized);
    })
    .map((entry, row_index) => ({
      key: `search-${entry.category_id}-${entry.sub_category_id}-${row_index}`,
      title: entry.title,
      sub_category_item_ids: entry.item_ids,
      breadcrumb: entry.category_title,
    }));
}

export function countCategorySelections(category: CatalogCategory, selected_ids: Set<number>): number {
  let count = 0;
  category.subCategories.forEach(sub_category => {
    sub_category.items.forEach(item => {
      if (selected_ids.has(item.id)) {
        count += 1;
      }
    });
  });
  return count;
}
